import { Chip } from "@homebound/beam";
import React from "react";
import { IconKey } from "src/components/Icon";
// eslint-disable-next-line @homebound/prevent-test-file-imports-in-app-code
import { type ProjectTypeName } from "src/generated/graphqlFactories";
import { possibleTypes, ProjectStatus, Stage } from "src/generated/graphqlTypes";

const contractualStages = [Stage.PreConExecution, Stage.Construction];
export function isContractualStage(stage: Stage): boolean {
  return contractualStages.includes(stage);
}

export const nonClosedProjects = [ProjectStatus.Active, ProjectStatus.Completed, ProjectStatus.UnderWarranty];

export function isProjectType(value: { __typename?: string } | undefined): value is { __typename: ProjectTypeName } {
  if (!value || !value.__typename) return false;
  return possibleTypes.Project.includes(value.__typename);
}

export function getProjectTypeIcon(value: { __typename?: string } | undefined): IconKey {
  if (!value || !value.__typename) return "house";
  const projectTypeIconPerValue: Record<ProjectTypeName, IconKey> = {
    Region: "mapGlobe",
    Market: "buildings",
    Community: "community",
    Subdivision: "subdivision",
    Lot: "lot",
    StandaloneProject: "houseFilled",
    Structure: "houseFilled",
    Unit: "houseFilled",
  };

  return projectTypeIconPerValue[value.__typename as ProjectTypeName] || "house";
}

export function getProjectEntityTypeName(value: { __typename?: string } | undefined): string | undefined {
  if (!value || !value.__typename) return undefined;

  const entityTypeNamePerValue: Record<ProjectTypeName, string> = {
    StandaloneProject: "Project", // standalone projects are not hierarchical, so we return just 'Project'
    Region: "Region",
    Market: "Market",
    Community: "Community",
    Subdivision: "Subdivision",
    Lot: "Lot",
    Structure: "Structure",
    Unit: "Unit",
  };
  return entityTypeNamePerValue[value.__typename as ProjectTypeName];
}

/**
 * Returns a hierarchy label for non-standalone projects.
 * Returns null for standalone projects (which don't need a label).
 */
export function getProjectHierarchyLabel(project: { __typename?: string }): string | null {
  const entityTypeName = getProjectEntityTypeName(project);
  if (!entityTypeName || entityTypeName === "Project") return null;
  return entityTypeName;
}

/**
 * Returns a React node for displaying project name with hierarchy chip in dropdowns/menus.
 * For standalone projects, returns just the name.
 * For hierarchical projects (Lot/Structure), returns name with a compact chip.
 */
export function getProjectOptionMenuLabel(project: { name: string; __typename?: string }): React.ReactNode {
  const entityTypeName = getProjectEntityTypeName(project);
  if (!entityTypeName || entityTypeName === "Project") return project.name;
  return (
    <>
      {project.name}
      <Chip text={entityTypeName} compact title={entityTypeName} />
    </>
  );
}

export function getProjectsAndDevelopmentMenuLabel(projectOrDev: {
  name: string;
  __typename?: string;
}): React.ReactNode {
  if (projectOrDev.__typename === "Development") {
    return (
      <>
        {projectOrDev.name}
        <Chip text="Development" compact title={"Development"} />
      </>
    );
  }
  return getProjectOptionMenuLabel(projectOrDev);
}
